/**
 * Overlay-visibility tracker.
 *
 * The AdMob banner is a NATIVE view drawn on top of the WebView — that means
 * every DOM element, including our own Dialog/Sheet overlays, renders BENEATH
 * it. To keep the banner from covering popovers we watch the document for any
 * Radix Dialog / Vaul Drawer that's currently open and let subscribers react
 * (typically by calling `AdMob.hideBanner()` / `AdMob.resumeBanner()`).
 *
 * We use a MutationObserver rather than wiring every Dialog/Sheet component so
 * that new overlay libraries (Radix Popover, DropdownMenu, etc.) get the same
 * treatment automatically — as long as their open state is reflected on a
 * portalled element via `role="dialog"` or the `vaul-drawer` attribute.
 */

type Listener = (anyOverlayOpen: boolean) => void;

const listeners = new Set<Listener>();
let observer: MutationObserver | null = null;
let lastState = false;

/** True when at least one Radix Dialog or Vaul Drawer is currently open. */
export function isAnyOverlayOpen(): boolean {
  if (typeof document === "undefined") return false;
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [vaul-drawer][data-state="open"]',
  );
}

function emit() {
  const now = isAnyOverlayOpen();
  if (now === lastState) return;
  lastState = now;
  for (const l of listeners) l(now);
}

function ensureObserver() {
  if (observer || typeof document === "undefined") return;
  observer = new MutationObserver(() => emit());
  // Overlays are portalled to <body>; watching subtree there catches both the
  // insertion (childList) and the open/close toggle (data-state attribute).
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["data-state", "role"],
  });
}

/**
 * Subscribe to overlay visibility changes. The callback fires immediately with
 * the current state and again whenever it flips. Returns an unsubscribe fn.
 */
export function onOverlayVisibilityChange(cb: Listener): () => void {
  ensureObserver();
  listeners.add(cb);
  cb(isAnyOverlayOpen());
  return () => {
    listeners.delete(cb);
  };
}
